import { Link } from 'react-router-dom'
import { Avatar } from '@/components/Avatar'
import { Badge } from '@/components/Badge'
import { Icon } from '@/components/Icon'
import { formatDaysLate } from '@/lib/dates'
import { CALLER_STATUS_META } from './api'
import { RESULT_ROW, RESULT_ROW_CHEVRON } from '@/components/ResultRow'

interface Caller {
  id: string
  full_name: string
  phone: string | null
  place: string | null
  days_overdue: number
  status: keyof typeof CALLER_STATUS_META
}

/**
 * One enquiry, shaped exactly like a patient result row.
 *
 * The two sit side by side on the dashboard, so anything they do differently —
 * an avatar on one and not the other, a row that starts left of its own card
 * title — reads as a mistake rather than a distinction.
 */
export function CallerResultRow({ caller }: { caller: Caller }) {
  const meta = CALLER_STATUS_META[caller.status]
  const secondary = [caller.phone, caller.place].filter(Boolean).join(' · ')

  return (
    <li>
      <Link
        to={`/callers/${caller.id}`}
        className={RESULT_ROW}
        aria-label={`${caller.full_name}${secondary ? `, ${secondary}` : ''}, ${
          caller.days_overdue > 0 ? formatDaysLate(caller.days_overdue) : meta.label
        }`}
      >
        <Avatar name={caller.full_name} size={36} />
        <span className="flex min-w-0 flex-1 flex-col gap-px">
          <span className="truncate text-base leading-tight font-semibold">
            {caller.full_name}
          </span>
          {secondary && (
            <span className="truncate text-xs leading-snug text-fg-muted">{secondary}</span>
          )}
        </span>
        {/* Words, never a colour alone — readable to a colour-blind reader
            under fluorescent light. */}
        <Badge tone={caller.days_overdue > 0 ? 'warning' : meta.tone}>
          {caller.days_overdue > 0 ? formatDaysLate(caller.days_overdue) : meta.label}
        </Badge>
        <span className={RESULT_ROW_CHEVRON}>
          <Icon name="chevronRight" size="1rem" />
        </span>
      </Link>
    </li>
  )
}
